import { Box, Flex, TabPanel } from '@televet/kibble-ui/build/chakra';
import { TabProps } from '@televet/kibble-ui/build/components/Tabs/components/Tab';
import { TabsLayout } from '@televet/kibble-ui/build/components/TabsLayout';
import { useTracking } from 'shared/providers/TrackingProvider';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import FilterIconButton from 'shared/components/FilterIconButton/FilterIconButton';
import { TasksEvents } from 'shared/providers/TrackingProvider/EventNames';
import useClinicUser from 'shared/hooks/useClinicUser';
import { TaskStatus } from 'shared/types/graphql';
import { taskGroupsMap } from '../constants';
import { useTasksContext } from '../context/TasksContext';
import { TaskList } from '../types';
import { findTabGroupNodeByIndex } from '../utils';
import { TaskFilter } from './TaskFilter';
import { TaskGroupList } from './TaskGroupList';

const openAndInProgressStatuses = [TaskStatus.Open, TaskStatus.InProgress];
const completedTasksOnlyStatuses = [TaskStatus.Completed];

type TaskTabsLayoutProps = {
  taskLists: TaskList[];
};

export const TaskTabsLayout = ({ taskLists }: TaskTabsLayoutProps): JSX.Element => {
  const { trackEvent } = useTracking();
  const { currentClinicId } = useClinicUser();
  const [isFilterPopoverOpen, setIsFilterPopoverOpen] = useState(false);
  const { hasFilters, saveFilters, activeTab, setActiveTab } = useTasksContext();

  const tabList = useMemo(() => {
    const tabs: (TabProps & {
      'data-testid': string;
    })[] = [
      {
        label: 'Needs Attention',
        'data-testid': 'tasks-needs-attention-tab',
      },
      {
        label: 'Completed',
        'data-testid': 'tasks-completed-tab',
      },
    ];
    return tabs;
  }, []);

  useEffect(() => {
    // By default the first tab is selected
    trackEvent(TasksEvents.NeedsAttentionTabViewed, {
      clinicId: currentClinicId,
    });
  }, [currentClinicId, trackEvent]);

  const handleTabChange = useCallback(
    (index: number): void => {
      const tab = findTabGroupNodeByIndex(index);
      setActiveTab(tab);
      trackEvent(
        tab === taskGroupsMap.needsAttention ? TasksEvents.NeedsAttentionTabViewed : TasksEvents.CompletedTabViewed,
        {
          clinicId: currentClinicId,
        },
      );
    },
    [currentClinicId, setActiveTab, trackEvent],
  );

  return (
    <Box position="relative">
      <TabsLayout
        tabList={tabList}
        isManual={true}
        index={activeTab.tabIndex}
        onChange={handleTabChange}
        tabListProps={{
          position: 'sticky',
          bg: 'white',
        }}
        tabPanels={[
          <TabPanel key="needs-attention" pt={5} px={4}>
            {activeTab === taskGroupsMap.needsAttention && (
              <TaskGroupList taskLists={taskLists} taskStatuses={openAndInProgressStatuses} />
            )}
          </TabPanel>,
          <TabPanel key="completed" pt={4} px={4}>
            {activeTab == taskGroupsMap.completed && (
              <TaskGroupList
                taskLists={taskLists}
                taskStatuses={completedTasksOnlyStatuses}
                completedTasksOnly={true}
              />
            )}
          </TabPanel>,
        ]}
      />
      <Flex gap={2} position="absolute" top={1.5} right={5} zIndex={10}>
        <TaskFilter
          trigger={
            <FilterIconButton
              data-testid="tasks-filter-toggle-button"
              buttonProps={{ 'data-testid': 'tasks-filter-toggle-button' }}
              showBadge={hasFilters}
              onFilterToggle={(): void => setIsFilterPopoverOpen((value) => !value)}
            />
          }
          isOpen={isFilterPopoverOpen}
          activeGroup={activeTab}
          onClose={(): void => {
            saveFilters();
            setIsFilterPopoverOpen(false);
          }}
        />
      </Flex>
    </Box>
  );
};
